import type { LanguageCode } from '@biteinc/enums';

export type SimpleStringOptions = {
  allowCommas?: boolean;
  allowLineBreaks?: boolean;
  maxLength?: number;
  specialCharacters?: string;
  inverse?: boolean;
  rejectEmptySpace?: boolean;
  allowDiacritics?: boolean;
};

// Allow any printable ASCII for QR code support.
// Relaxed from Code 39 to support QR codes with alphanumeric values.
// Tradeoff: Bureau won't warn users entering invalid barcode formats.
export const barcodeRegexString = '^[\\x20-\\x7E]+$';
export const hexColorWithOptionalOpacityRegexString = '^#([A-F0-9]{6})([A-F0-9]{2}){0,1}$';

export function simpleStringRegexString(options: SimpleStringOptions = {}): string {
  const {
    maxLength,
    specialCharacters = '',
    inverse = false,
    allowCommas = false,
    allowLineBreaks = false,
    rejectEmptySpace = false,
    allowDiacritics = false,
  } = options;

  const lengthRestriction = maxLength ? `{1,${maxLength}}` : '+';
  const commasAndSpecialChars = allowCommas ? `,${specialCharacters}` : specialCharacters;
  const lineBreaks = allowLineBreaks ? '\\r\\n' : '';

  if (rejectEmptySpace) {
    if (inverse) {
      // Doesn't make sense to inverse non-empty space strings
      throw new Error('Regex inverse option is not supported with rejectEmptySpace option.');
    }
    // Allowed characters (including space, hyphen, dot, configured extras)
    const allowedChars = `a-zA-Z0-9${
      allowDiacritics ? 'À-ž' : ''
    } \\-\\.${commasAndSpecialChars}${lineBreaks}`;

    // Base regex: matches one or more allowed characters
    const baseRegex = `[${allowedChars}]+`;

    const emptyChars = `\\s\\-\\.`;

    // Lookahead to ensure string contains non-whitespace and is not just '-' or '.'
    const nonWhitespaceLookahead = `(?=.*[^${emptyChars}])`;

    // Lookahead for length constraint (only if maxLength is provided)
    const lengthLookahead = maxLength ? `(?=^.{1,${maxLength}}$)` : '';

    // Combine parts: ensure non-whitespace, enforce length (if needed), match allowed chars
    return `^${nonWhitespaceLookahead}${lengthLookahead}${baseRegex}$`;
  }

  const regexBody = `a-zA-Z0-9${
    allowDiacritics ? 'À-ž' : ''
  } \\-\\.${commasAndSpecialChars}${lineBreaks}`;
  return inverse ? `[^${regexBody}]` : `^[${regexBody}]${lengthRestriction}$`;
}

export const ipAddressRegexString = '^\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}$';
export const ipAddressWithPortRegexString = '^\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}:\\d{1,5}$';

// Verifone VISTA schema version (begin-session `version` attribute, e.g. "01.05.00").
export const vistaSchemaVersionRegexString = '^\\d{2}\\.\\d{2}\\.\\d{2}$';

export function roomNumberRegexString(): string {
  return simpleStringRegexString({ maxLength: 10, rejectEmptySpace: true });
}

export const addressLineRegex = simpleStringRegexString({
  specialCharacters: `:&#,'"()+/`,
  allowDiacritics: true,
});

export const siteStoreNumberRegexString = '^[A-Z0-9-]+$';

// Proper checks sanctioned by USPS, Canada Post, and Royal Mail.
// US ZIP (5 digits) | Canadian postal code (A1A 1A1) | UK postcode (e.g. SW1A 1AA, M1 1AE).
export const postalCodeRegexString =
  '^(\\d{5}|([ABCEGHJ-NPRSTVXY]\\d[ABCEGHJ-NPRSTV-Z]\\s?\\d[ABCEGHJ-NPRSTV-Z]\\d)|([A-Za-z]{1,2}\\d[A-Za-z\\d]?\\s?\\d[A-Za-z]{2}))$';

export const smsRegexString =
  '^[\\sa-zA-Z0-9\\!\\@\\#\\$\\%\\^\\&\\*\\(\\)\\,\\.\\/\\<\\>\\?\\;\\\'\\:\\"\\[\\]\\{\\}\\-\\+\\_\\=\\~]{0,160}$';

// generic email regex
export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// min 1 number; min 1 uppercase; min 1 lowercase; between 14 & 32 in length
export const passwordValidationRegex = '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z]).{14,32}$';

// validate semvar version of build name
// regex source: https://semver.org/#is-there-a-suggested-regular-expression-regex-to-check-a-semver-string
export const releasableBuildNameRegexString =
  '^biteinc-(chromeos|garcon|gcn|vitrine)\\.([0-9]|[1-9][0-9]*)\\.([0-9]|[1-9][0-9]*)\\.([0-9]|[1-9][0-9]*)(-([0-9A-Za-z-]+)\\.([0-9]|[1-9][0-9]*)){0,1}$';

export const generalAvailabilityBuildNameRegexString =
  /^biteinc-(chromeos|garcon|gcn|vitrine).[0-9]+.[0-9]+.[0-9]+$/;

export function getTokenMatchesFromStringSpec(
  stringSpec: { [code in LanguageCode]?: string },
  languageCode: LanguageCode,
): string[] {
  return (stringSpec[languageCode] || '').match(/\{\{\$[0-9].*?\}\}/g) || [];
}

export function isEmail(input?: string): boolean {
  return emailRegex.test(input || '');
}

// Validate that we have a token from $1 to $xx but not necessarily in that order.
export function tokenMatchesAreValid(tokenMatches: string[]): boolean {
  for (let i = 0; i < tokenMatches.length; i++) {
    const hasTokenMatchForNumber = tokenMatches.some((match) => {
      return match.startsWith(`{{$${i + 1}`);
    });
    if (!hasTokenMatchForNumber) {
      return false;
    }
  }
  return true;
}

const PHONE_NUMBER_LENGTH = 10;

const numberRegex = /^\d+$/;
const consecutiveNumbersRegex =
  /^((?:0(?=1|$))?(?:1(?=2|$))?(?:2(?=3|$))?(?:3(?=4|$))?(?:4(?=5|$))?(?:5(?=6|$))?(?:6(?=7|$))?(?:7(?=8|$))?(?:8(?=9|$))?9?|(?:9(?=8|$))?(?:8(?=7|$))?(?:7(?=6|$))?(?:6(?=5|$))?(?:5(?=4|$))?(?:4(?=3|$))?(?:3(?=2|$))?(?:2(?=1|$))?(?:1(?=0|$))?0?)$/;
const repeatedNumbersRegex = /\b(\d)\1+\b/;
const validAreaCodeRegex = /^([2-9][0-8][0-9])$/;

export function isPhone(value: string | undefined): boolean {
  return !(
    !value ||
    value.length < PHONE_NUMBER_LENGTH ||
    value.length > PHONE_NUMBER_LENGTH ||
    // consecutive numbers meaning sequentially increasing or decreasing
    // e.g. 1234567890, 9876543210
    consecutiveNumbersRegex.test(value) ||
    // repeated numbers meaning the same number appears multiple times in a row
    // e.g. 1111111111, 2222222222
    repeatedNumbersRegex.test(value) ||
    // valid area code meaning the first three digits are between 2-9 and the rest are 0-9
    // e.g. 2015551234, 9085551234
    !validAreaCodeRegex.test(value.slice(0, 3)) ||
    !numberRegex.test(value)
  );
}

export const deliveryAddressCityValidationRegex = simpleStringRegexString({
  allowDiacritics: true,
});

export const deliveryAddressStateValidationRegex = simpleStringRegexString();

export const deliveryAddressPostalCodeValidationRegex = postalCodeRegexString;

export const deliveryInstructionsValidationRegex = simpleStringRegexString({
  allowCommas: true,
  allowLineBreaks: true,
});

export const guestNameValidationRegex = simpleStringRegexString({
  rejectEmptySpace: true,
  maxLength: 30,
});

export const guestVehicleValidationRegex = simpleStringRegexString();

export const outpostRoomNumberValidationRegex = roomNumberRegexString();

export const tableNumberValidationRegex = simpleStringRegexString({
  rejectEmptySpace: true,
});
